import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedList, FormattedMessage, FormattedNumber } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { Button } from 'ui-components/components/button';
import { Text, TextType } from 'ui-components/components/text';
import { useToggle } from 'ui-components/hooks/use-toggle';

import style from './bulk-rate-recommendations.module.less';
import messages from './messages';
import { RateRecommendationBanner } from './rate-recommendation-banner';
import { useOrdersGridRequestFilters, useRecommendationNames } from './use-rate-recommendations';
import FormattedCurrency from '../../../components/formatted-currency';
import { Unknown } from '../../../constants/currency-codes';
import { emptyArray, emptyObject } from 'ui-components/default-props/empty';
import { useEnableBulkRecommendedRatesOnSuperSelect } from '../../../feature-flags/hooks/use-enable-bulk-recommended-rates-on-super-select';
import { useEnableSwitchAndSaveRateRecommendations } from '../../../feature-flags/hooks/use-enable-switch-and-save-rate-recommendations';
import { useIsBulkRateShopperRecommendationsEnabled } from '../../../hooks/use-is-bulk-rate-shopper-recommendations-enabled';
import Global from '../../../store/global-selectors';
import { bulkApplyRecommendations } from '../../../store/orders/drawer/orders.drawer.actions';
import { getSummary } from '../rate-recommendations.api';

/**
 * @param {object} props
 * @param {string[]} props.fulfillmentPlanIds
 * @param {boolean} props.isLoadingRates
 * @returns {React.JSX.Element}
 */
export const BulkRateRecommendations = ({ fulfillmentPlanIds = emptyArray, isLoadingRates = false }) => {
  const isSwitchAndSaveRecommendationsEnabled = useEnableSwitchAndSaveRateRecommendations();
  if (!isSwitchAndSaveRecommendationsEnabled) {
    return null;
  }

  return <BulkRateRecommendationsContent fulfillmentPlanIds={fulfillmentPlanIds} isLoadingRates={isLoadingRates} />;
};

const BulkRateRecommendationsContent = ({ fulfillmentPlanIds = emptyArray, isLoadingRates = false }) => {
  const { isActivated, deactivate } = useToggle(true);
  const { pathname } = useLocation();
  const gridApiFilters = useOrdersGridRequestFilters(undefined, pathname);
  const enableBulkRecommendedRatesOnSuperSelect = useEnableBulkRecommendedRatesOnSuperSelect();
  const isBulkRateShopperRecommendationsEnabled = useIsBulkRateShopperRecommendationsEnabled();

  const [bulkApplyRecommendationsJobId, setBulkApplyRecommendationsJobId] = useState('');
  const [appliedRecommendationJobId, setAppliedRecommendationJobId] = useState('');
  const [totalSavings, setTotalSavings] = useState({ value: 0, code: Unknown });
  const [uniqueInsuranceProviderIds, setUniqueInsuranceProviderIds] = useState([]);
  const [uniqueServiceIds, setUniqueServiceIds] = useState([]);
  const [uniqueServiceCollectionGuids, setUniqueServiceCollectionGuids] = useState([]);
  const [fulfillmentPlansWithRecommendationsCount, setFulfillmentPlansWithRecommendationsCount] = useState(0);
  const [summaryRequestError, setSummaryRequestError] = useState(null);

  const alternativeRateJobs = useSelector(Global.selectOrdersGridAlternativeRateJobs);
  const recommendationNames = useRecommendationNames(uniqueServiceIds, uniqueInsuranceProviderIds, uniqueServiceCollectionGuids);
  const { isApplyingRecommendations, appliedSavings, appliedSuccessfully } = useSelector((state) => {
    const lastAppliedRecommendation = Global.selectOrdersDrawerLastAppliedRecommendation(state);

    return appliedRecommendationJobId && lastAppliedRecommendation?.jobId === appliedRecommendationJobId
      ? lastAppliedRecommendation
      : emptyObject;
  });

  const isLoadingAlternativeRates = alternativeRateJobs?.length > 0;

  const dispatch = useDispatch();

  const getRecommendationSummary = useCallback(() => {
    setBulkApplyRecommendationsJobId('');
    setTotalSavings({ value: 0, code: Unknown });
    setUniqueInsuranceProviderIds([]);
    setUniqueServiceIds([]);
    setUniqueServiceCollectionGuids([]);
    setFulfillmentPlansWithRecommendationsCount(0);
    setSummaryRequestError(null);

    const request = enableBulkRecommendedRatesOnSuperSelect ? gridApiFilters : { fulfillmentPlanIds };
    request.isRateShopperRecommendationsEnabled = isBulkRateShopperRecommendationsEnabled;

    getSummary(request)
      .then((response) => {
        if (!response.data) {
          setSummaryRequestError(true);
          return;
        }

        const {
          bulkUpdateJobGuid: _bulkUpdateJobGuid,
          totalSavings: _totalSavings,
          insuranceRateRecommendations,
          serviceRateRecommendations,
          rateShopperRecommendations = emptyArray,
          fulfillmentPlansWithRecommendationsCount: _fulfillmentPlansWithRecommendationsCount,
        } = response.data;

        if (_fulfillmentPlansWithRecommendationsCount === 0) {
          return;
        }

        setBulkApplyRecommendationsJobId(_bulkUpdateJobGuid);
        setTotalSavings(_totalSavings);

        const insuranceProviderIds = insuranceRateRecommendations.map((recommendation) => `${recommendation.toInsuranceProviderId}`);
        setUniqueInsuranceProviderIds(Array.from(new Set(insuranceProviderIds)));

        const serviceIds = serviceRateRecommendations.map((recommendation) => `${recommendation.toServiceId}`);
        setUniqueServiceIds(Array.from(new Set(serviceIds)));

        const serviceCollectionGuids = rateShopperRecommendations.map((recommendation) => recommendation.toServiceCollectionGuid);
        setUniqueServiceCollectionGuids(Array.from(new Set(serviceCollectionGuids)));

        setFulfillmentPlansWithRecommendationsCount(_fulfillmentPlansWithRecommendationsCount);
      })
      .catch(() => {
        setSummaryRequestError(true);
      });
  }, [enableBulkRecommendedRatesOnSuperSelect, fulfillmentPlanIds, gridApiFilters, isBulkRateShopperRecommendationsEnabled]);

  // Reset job ids when fulfillment plans change
  useEffect(() => {
    setBulkApplyRecommendationsJobId('');
    setAppliedRecommendationJobId('');
  }, [fulfillmentPlanIds]);

  // When recommendations have finished applying, get new summary then hide the applied state after 3 seconds
  useEffect(() => {
    if (appliedSuccessfully && appliedSavings?.value > 0) {
      getRecommendationSummary();

      setTimeout(() => {
        setAppliedRecommendationJobId('');
      }, 3000);
    }
  }, [appliedSavings?.value, appliedSavings?.code, getRecommendationSummary, appliedSuccessfully]);

  // On load or when isLoadingAlternativeRates goes from true to false, get new summary
  // This ensures that the alternative rates are up to date before getting a new recommendation summary
  useEffect(() => {
    if (!isActivated || isLoadingAlternativeRates) {
      return;
    }

    getRecommendationSummary();
  }, [getRecommendationSummary, isActivated, isLoadingAlternativeRates]);

  const applyRecommendations = useCallback(() => {
    dispatch(bulkApplyRecommendations(bulkApplyRecommendationsJobId, isBulkRateShopperRecommendationsEnabled));
    setAppliedRecommendationJobId(bulkApplyRecommendationsJobId);
  }, [bulkApplyRecommendationsJobId, dispatch, isBulkRateShopperRecommendationsEnabled, setAppliedRecommendationJobId]);

  const shouldShowBulkRateRecommendations = useMemo(() => {
    if (!isActivated) {
      return false;
    }

    if (isApplyingRecommendations || appliedRecommendationJobId) {
      return true;
    }

    if (isLoadingRates || isLoadingAlternativeRates || summaryRequestError) {
      return false;
    }

    return fulfillmentPlansWithRecommendationsCount > 0;
  }, [
    isActivated,
    isApplyingRecommendations,
    appliedRecommendationJobId,
    isLoadingRates,
    isLoadingAlternativeRates,
    summaryRequestError,
    fulfillmentPlansWithRecommendationsCount,
  ]);

  if (!shouldShowBulkRateRecommendations) {
    return null;
  }

  const bannerText = (
    <Text
      formattedMessageValues={{
        recommendationCount: <FormattedNumber value={fulfillmentPlansWithRecommendationsCount} />,
        recommendationList: <FormattedList type='conjunction' value={recommendationNames} />,
        s: (chunks) => <strong>{chunks}</strong>,
        savingsAmount: (
          <strong>
            <FormattedCurrency currencyCode={totalSavings.code} value={totalSavings.value} />
          </strong>
        ),
      }}
      textType={TextType.Paragraph}
      value={messages.bulkRecommendationBannerText}
    />
  );

  return (
    <RateRecommendationBanner
      appliedSavings={appliedSavings}
      applyButton={
        <Button onClick={applyRecommendations} theme='white'>
          <FormattedMessage {...messages.recommendationApplyButton} />
        </Button>
      }
      bannerText={bannerText}
      isApplyingSavings={isApplyingRecommendations}
      loadingIndicatorContainerClassName={style['bulk-recommendation-banner-loading-indicator-container']}
      onDismiss={deactivate}
      theme='light'
    />
  );
};
